import { useTranslation } from 'react-i18next';
import type { PageType } from 'types';
import type { UpcomingProductsModule } from 'types/capi/modules';

import PopularReleasesCarousel from '~/components/PopularReleasesCarousel';
import Tracked from '~/components/Tracked';
import { MODULE_CTA_POSITION } from '~/events/NavigationLinkInteractionEvent';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import useRegion from '~/hooks/useRegion';

const MODULE_TYPE = 'homepage-upcoming-products';

type Props = {
  items: UpcomingProductsModule['items'];
  modulePosition: number;
  pageType: PageType;
};

const VIEW_MORE_PATH = '/release-dates/?sort=UPCOMING_RELEASES';

function ViewMoreButton({ modulePosition }: { modulePosition: number }) {
  const { t } = useTranslation('common');
  const fireNavigationLink = useNavigationLinkInteraction('module_cta');
  const label = t('viewMore');

  return (
    <div className="w-36">
      <Tracked.InternalLink
        variant="transparent-black-border"
        className="flex items-center justify-center"
        pagePosition="Homepage Upcoming Releases"
        name="VIEW_MORE"
        to={VIEW_MORE_PATH}
        onClick={() =>
          fireNavigationLink(VIEW_MORE_PATH, MODULE_CTA_POSITION, {
            label,
            modulePosition,
            moduleType: MODULE_TYPE
          })
        }
      >
        {label}
      </Tracked.InternalLink>
    </div>
  );
}

export default function UpcomingProducts({
  items,
  modulePosition,
  pageType
}: Props) {
  const region = useRegion();
  const { t } = useTranslation('home');

  const allowNotifications = region.code === 'GB';

  return (
    <div className="w-full bg-grey-tile">
      <div className="flex flex-col items-center justify-center gap-6 py-10 md:gap-8 md:py-20">
        {items && (
          <PopularReleasesCarousel
            products={items}
            title={t('upcomingReleases')}
            description={t('upcomingDescription')}
            showIcon={false}
            pagePosition="Homepage Upcoming Releases"
            modulePosition={modulePosition}
            moduleType={MODULE_TYPE}
            pageType={pageType}
            allowNotifications={allowNotifications}
            showBackground={false}
            overrideInteractionPagePosition
          />
        )}
        <ViewMoreButton modulePosition={modulePosition} />
      </div>
    </div>
  );
}
